import React from 'react';

import { format } from '@eika-infrastruktur/date';
import { formatAccountNumber } from '@eika-infrastruktur/format';
import { copyToClipboard } from '@eika-infrastruktur/utils';
import { DetailsList, DetailsListItem } from '@eika/details-list';
import { IconTypes } from '@eika/icon';

import { useConfig } from '../../defaultConfig';
import type { Account, AccountWithdrawalDetails } from '../../generated-types';
import useTranslation, { format as translationFormat } from '../../hooks/useTranslation';
import getFreeWithdrawalStatus from '../../utils/mapper/getFreeWithdrawalStatus';
import AccountCrossborderInfo from './AccountCrossborderInfo';

import './AccountDetailsView.scss';

interface Props {
    account?: Account;
    freeWithdrawals?: AccountWithdrawalDetails;
    isLoading: boolean;
}

const labelDateFormatYear = 'dd. MMMM yyy';
const AccountDetailsView: React.FC<Props> = ({ account, freeWithdrawals, isLoading }) => {
    const translated = useTranslation();
    const { featureToggles } = useConfig();

    const { showFreewithdrawal, remainingWithdrawals } = getFreeWithdrawalStatus(freeWithdrawals);

    return (
        <div className="account-details-view__container">
            <DetailsList title={translated.accountDetails.details} isLoading={isLoading} skeletonCount={5}>
                {account?.type && (
                    <DetailsListItem
                        label={translated.accountDetails.accountType}
                        value={translated.accountDetails.mappedAccountType[account.type]}
                    />
                )}

                {account?.accountProductName && (
                    <DetailsListItem
                        label={translated.accountDetails.accountProductName}
                        value={account.accountProductName}
                    />
                )}
                {account?.opened && (
                    <DetailsListItem
                        label={translated.accountDetails.openedDate}
                        value={format(account.opened, labelDateFormatYear)}
                    />
                )}

                <DetailsListItem
                    label={translated.withdrawals.freeWithdrawals}
                    value={translationFormat(
                        translated.withdrawals.freeWithdrawalsOf,
                        remainingWithdrawals,
                        freeWithdrawals?.totalNumberOfFreeWithdrawals ?? 0,
                    )}
                    show={showFreewithdrawal}
                />
                <DetailsListItem
                    label={translated.accountNumber}
                    value={formatAccountNumber(account?.accountNumber)}
                    icon={IconTypes.COPY_24}
                    button={{
                        onClick: () => copyToClipboard(account?.accountNumber.value),
                        title: translationFormat(
                            translated.accountDetails.copyAccountNumber,
                            account?.accountNumber.value ?? '',
                        ),
                    }}
                />
                {!featureToggles.disableCrossborderInfo && account && <AccountCrossborderInfo account={account} />}
            </DetailsList>
        </div>
    );
};

export default AccountDetailsView;
